import { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Search, CheckCircle2, PhoneCall } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { SERVED_CITIES, SERVED_ZIPS, PHONE_DISPLAY, PHONE_TEL } from "@/data/site";

const POINTS = [
  { name: "Lodi", x: 250, y: 118 },
  { name: "Stockton", x: 205, y: 205 },
  { name: "French Camp", x: 250, y: 268 },
  { name: "Ripon", x: 300, y: 372 },
  { name: "Tracy", x: 138, y: 335 },
  { name: "Lathrop", x: 215, y: 320 },
  { name: "Modesto", x: 355, y: 428 },
  { name: "Turlock", x: 412, y: 490 },
  { name: "Oakdale", x: 448, y: 330 },
  { name: "Escalon", x: 370, y: 330 },
];

const ServiceMap = () => {
  const [query, setQuery] = useState("");
  const [result, setResult] = useState(null);

  const check = (e) => {
    e.preventDefault();
    const q = query.trim().toLowerCase();
    if (!q) return;
    const hit =
      SERVED_ZIPS.includes(q) ||
      SERVED_CITIES.some((c) => c === q || c.includes(q) || q.includes(c));
    setResult(hit ? "yes" : "maybe");
  };

  return (
    <section data-testid="service-map-section" className="relative overflow-hidden bg-bear-ink py-24 lg:py-32">
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.35]"
        style={{ background: "radial-gradient(ellipse at 30% 40%, rgba(0,168,225,0.14), transparent 55%)" }}
      />
      <div className="relative mx-auto grid max-w-7xl items-center gap-16 px-4 sm:px-6 lg:grid-cols-2">
        <div>
          <Reveal>
            <p className="font-head text-xs font-bold uppercase tracking-[0.28em] text-bear-cyan">
              Coverage
            </p>
            <h2 className="mt-4 font-display text-5xl uppercase leading-[0.95] tracking-wide text-white sm:text-6xl">
              60 Miles.
              <span className="block text-stroke-cyan">Zero Excuses.</span>
            </h2>
            <p className="mt-6 max-w-md text-base leading-relaxed text-white/60">
              From our home base on Shaefer Street in Manteca, the Bear covers a
              full 60-mile radius across the Central Valley — dispatched fast,
              arriving when we say we will.
            </p>
          </Reveal>

          <Reveal delay={0.15}>
            <form onSubmit={check} className="mt-10" data-testid="area-checker-form">
              <label className="font-head text-xs font-bold uppercase tracking-[0.2em] text-white/50">
                Check your city or ZIP
              </label>
              <div className="mt-3 flex overflow-hidden rounded-full border border-white/15 bg-white/5 backdrop-blur focus-within:border-bear-cyan">
                <input
                  data-testid="area-checker-input"
                  value={query}
                  onChange={(e) => { setQuery(e.target.value); setResult(null); }}
                  placeholder="e.g. Tracy or 95376"
                  className="w-full bg-transparent px-5 py-4 text-sm text-white placeholder-white/35 outline-none"
                />
                <button
                  type="submit"
                  data-testid="area-checker-button"
                  className="flex shrink-0 items-center gap-2 bg-bear-cyan px-6 font-head text-sm font-bold uppercase tracking-wider text-bear-ink transition-colors hover:bg-white"
                >
                  <Search className="h-4 w-4" />
                  <span className="hidden sm:inline">Check</span>
                </button>
              </div>
              {result === "yes" && (
                <motion.p
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  data-testid="area-result-yes"
                  className="mt-4 flex items-center gap-2 text-sm font-semibold text-bear-cyan"
                >
                  <CheckCircle2 className="h-5 w-5" />
                  Yes — you're inside our 60-mile service radius. We roll to you.
                </motion.p>
              )}
              {result === "maybe" && (
                <motion.p
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  data-testid="area-result-maybe"
                  className="mt-4 flex items-center gap-2 text-sm font-semibold text-bear-gold"
                >
                  <PhoneCall className="h-5 w-5" />
                  You may be near our edge — call {PHONE_DISPLAY} and we'll confirm in seconds.
                </motion.p>
              )}
            </form>
          </Reveal>
        </div>

        <Reveal delay={0.1} className="mx-auto w-full max-w-[540px]">
          <svg viewBox="0 0 600 600" className="w-full" role="img" aria-label="60-mile service radius map around Manteca, CA">
            <defs>
              <radialGradient id="radarGlow" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="#00A8E1" stopOpacity="0.18" />
                <stop offset="100%" stopColor="#00A8E1" stopOpacity="0" />
              </radialGradient>
            </defs>
            <circle cx="300" cy="300" r="270" fill="url(#radarGlow)" />
            {[90, 180, 270].map((r) => (
              <circle key={r} cx="300" cy="300" r={r} fill="none" stroke="rgba(255,255,255,0.12)" strokeDasharray="4 6" />
            ))}
            <circle cx="300" cy="300" r="60" fill="none" stroke="#00A8E1" strokeWidth="1.5" opacity="0.7">
              <animate attributeName="r" values="50;270" dur="3.2s" repeatCount="indefinite" />
              <animate attributeName="opacity" values="0.7;0" dur="3.2s" repeatCount="indefinite" />
            </circle>
            <circle cx="300" cy="300" r="60" fill="none" stroke="#00A8E1" strokeWidth="1.5" opacity="0.7">
              <animate attributeName="r" values="50;270" dur="3.2s" begin="1.6s" repeatCount="indefinite" />
              <animate attributeName="opacity" values="0.7;0" dur="3.2s" begin="1.6s" repeatCount="indefinite" />
            </circle>
            <text x="300" y="300" textAnchor="middle" dy="292" fill="rgba(255,255,255,0.35)" fontSize="12" fontFamily="Space Grotesk" letterSpacing="3" transform="translate(0,-592)">
            </text>
            {POINTS.map((p) => (
              <g key={p.name}>
                <circle cx={p.x} cy={p.y} r="4" fill="#A1784E" />
                <text x={p.x} y={p.y - 12} textAnchor="middle" fill="rgba(255,255,255,0.65)" fontSize="13" fontFamily="Space Grotesk" fontWeight="600">
                  {p.name}
                </text>
              </g>
            ))}
            <g>
              <circle cx="300" cy="300" r="12" fill="#E52425" />
              <circle cx="300" cy="300" r="5" fill="#FCFCFC" />
              <text x="300" y="336" textAnchor="middle" fill="#FCFCFC" fontSize="15" fontWeight="700" fontFamily="Space Grotesk" letterSpacing="1">
                MANTECA HQ
              </text>
            </g>
            <text x="300" y="578" textAnchor="middle" fill="rgba(255,255,255,0.35)" fontSize="11" fontFamily="Space Grotesk" letterSpacing="4">
              60-MILE SERVICE RADIUS — CENTRAL CALIFORNIA
            </text>
          </svg>
        </Reveal>
      </div>
    </section>
  );
};

export default ServiceMap;
